import { useEffect } from "react";
import FloatingHeader from "../components/landingPage/floatingMenu";

function PeakScorePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-base-100">
      <FloatingHeader />

      {/* Hero Section - Mobile */}
      <section className="h-screen md:hidden hero-mobile w-screen flex  justify-center">
        <div className="max-w-sm mx-auto px-4 text-center pt-32">
          <h1 className="text-xl font-grotesk text-base-content font-light mb-2">
            Understanding the
          </h1>
          <h1 className="text-5xl text-base-content font-heading font-bold leading-tight mb-4">
            Peak Score
          </h1>
          <p className="text-md text-base-content font-grotesk leading-snug">
            A daily readiness signal designed to help athletes and coaches make
            better training decisions
          </p>
        </div>
      </section>

      {/* Hero Section - Desktop */}
      <section className="hidden md:flex h-screen hero-desktop items-center">
        <div className="max-w-6xl mx-auto w-full px-6">
          <div className="w-2/3 flex flex-col gap-6">
            <h1 className="text-2xl font-grotesk font-light text-base-content">
              Understanding the
            </h1>
            <h1 className="text-7xl font-heading font-bold text-base-content leading-tight">
              Peak Score
            </h1>
            <p className="text-2xl text-base-content font-grotesk leading-snug max-w-2xl">
              A daily readiness signal designed to help athletes and coaches
              make better training decisions using the athlete's own data
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-16">
        <div className="mb-8">
          <p className="text-base-content/70 text-sm font-grotesk">
            Written by Sam Leslie • February 10, 2026
          </p>
        </div>

        <div className="space-y-12 text-base-content leading-relaxed">
          {/* Introduction */}
          <section className="bg-base-200 p-8 md:p-12 rounded-2xl">
            <p className="text-xl mb-8 font-grotesk leading-relaxed">
              The Peak score reflects how prepared you are on a given day to
              tolerate training stress and express performance, based on three
              realities that matter most.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-base-100 p-6 rounded-xl shadow-sm">
                <h3 className="text-lg font-heading font-bold text-primary mb-2">
                  Your Wellness
                </h3>
                <p className="text-sm font-grotesk text-base-content/80">
                  How training is being experienced physically and mentally.
                </p>
              </div>
              <div className="bg-base-100 p-6 rounded-xl shadow-sm">
                <h3 className="text-lg font-heading font-bold text-primary mb-2">
                  Your Load Balance
                </h3>
                <p className="text-sm font-grotesk text-base-content/80">
                  What training you have been exposed to, compared to what you
                  are used to.
                </p>
              </div>
              <div className="bg-base-100 p-6 rounded-xl shadow-sm">
                <h3 className="text-lg font-heading font-bold text-primary mb-2">
                  Your HLR
                </h3>
                <p className="text-sm font-grotesk text-base-content/80">
                  How your physiology is responding over time.
                </p>
              </div>
            </div>
            <div className="bg-base-100 p-6 rounded-xl border-l-4 border-primary">
              <p className="text-lg font-grotesk text-base-content italic">
                "Are today's training demands aligned with your current state,
                relative to your own baseline?"
              </p>
            </div>
            <p className="mt-6 text-base-content/80 font-grotesk">
              What makes the Peak score powerful is not that it tracks a single
              variable. It combines multiple readiness inputs into one decision
              support signal, so athletes and coaches stop guessing from one
              metric and start seeing the whole picture.
            </p>
          </section>

          {/* Wellness Section */}
          <section>
            <h2 className="text-4xl font-bold font-heading mb-6 text-base-content">
              Wellness: How training is being experienced
            </h2>
            <p className="mb-4">
              At the centre of the Peak score is wellness. Daily wellness
              check-ins capture how you are experiencing training and life
              stress, often before changes show up in performance, illness, or
              injury risk.
            </p>
            <p className="mb-4">Each day, athletes rate the following areas:</p>
            <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
              <li>Sleep duration</li>
              <li>Sleep quality</li>
              <li>Mood</li>
              <li>Stress</li>
              <li>Health</li>
              <li>Physical perception</li>
              <li>Recovery (weighted more heavily)</li>
            </ul>
            <p className="mb-4">
              These questions were chosen because they are consistently
              sensitive to fatigue, overload, illness, and poor recovery.
              Importantly, PEAK^ does not treat them as isolated daily scores.
            </p>
            <p className="mb-4">
              Instead, today's wellness input is interpreted through your recent
              and longer term patterns.
            </p>
            <p className="mb-4">
              PEAK^ compares your most recent wellness trend (last 7 days) to
              your longer term wellness pattern (last 28 days). That comparison
              becomes a scaling factor, applied to the wellness score you enter
              today.
            </p>
            <p className="mb-4">
              This creates a smarter signal, because it prevents two common
              errors:
            </p>
            <ol className="list-decimal list-inside space-y-2 mb-4 ml-4">
              <li>
                If your wellness has generally been strong but today dips, the
                score does not overreact. One low day does not suddenly make you
                poorly recovered.
              </li>
              <li>
                If your wellness has been trending poorly but today feels good,
                the score does not falsely inflate. One good day does not erase
                accumulated fatigue or stress.
              </li>
            </ol>
            <p>
              The result is a wellness signal that is more stable, more
              individual, and more meaningful than a simple daily rating or
              rolling average.
            </p>
          </section>

          {/* Load Balance Section */}
          <section>
            <h2 className="text-4xl font-bold font-heading mb-6 text-base-content">
              Load Balance: Recent load in long term context
            </h2>
            <p className="mb-4">
              Alongside wellness sits Load Balance. This provides essential
              context for the training load you have been exposed to, because
              training load in isolation is not meaningful.
            </p>
            <p className="mb-4">
              The same session can be well tolerated by one athlete and
              disruptive for another, depending entirely on what that athlete
              has been exposed to recently and what they have adapted to over
              time.
            </p>
            <p className="mb-4">
              Load Balance is built from the relationship between acute load and
              chronic load.
            </p>

            <h3 className="text-2xl font-bold font-heading mb-3 mt-8 text-primary">
              Acute Training Load
            </h3>
            <p className="mb-4">
              Acute training load reflects what you have been exposed to
              recently. In PEAK^, this is calculated from the last 9 days, with
              a deliberate emphasis on the most recent sessions, especially the
              last few days. This reflects the reality that fatigue accumulates
              quickly and yesterday matters more than last week.
            </p>
            <p className="mb-4">
              To achieve this, PEAK^ applies a decay based weighting so that
              recent sessions carry more influence, while older sessions still
              contribute without dominating the calculation. This keeps the
              signal sensitive to meaningful change without overreacting to a
              single hard or easy day.
            </p>

            <h3 className="text-2xl font-bold font-heading mb-3 mt-8 text-primary">
              Chronic Training Load
            </h3>
            <p className="mb-4">
              Chronic training load reflects your longer term training history
              and acts as a proxy for accumulated fitness and tolerance. PEAK^
              calculates this across a longer timeframe, placing most emphasis
              on the recent 28 days, while still drawing from the previous 84
              days to capture training robustness.
            </p>
            <p className="mb-4">
              When combined, this creates a chronic load signal that reflects
              your recent training foundation across weeks, not days. This
              matters because fitness, tissue tolerance, and durability are
              built over time.
            </p>

            <div className="bg-blue-500/10 border-l-4 border-blue-500 p-4 my-6">
              <p className="font-semibold mb-2">A key point for coaches.</p>
              <p>
                Load Balance does not label training as good or bad, and it does
                not dictate what must happen next. It simply describes how
                demanding the current training period is relative to your
                preparation.
              </p>
              <p className="mt-2">
                That single piece of context is one of the most useful inputs
                for timing hard sessions, progressing load intelligently, and
                identifying when risk may be rising.
              </p>
            </div>

            <h3 className="text-2xl font-bold font-heading mb-3 mt-8 text-primary">
              Load Balance accuracy over time
            </h3>
            <p className="mb-4">
              Load Balance becomes most representative after 84 days of
              consistent training data. Until then, PEAK^ uses modified
              calculations based on the history available. Early scores can be
              more volatile because the system is still learning your true
              baseline.
            </p>
            <p>
              As the training history builds, the signal becomes progressively
              more stable, more individual, and more accurate.
            </p>
          </section>

          {/* HLR Section */}
          <section>
            <h2 className="text-4xl font-bold font-heading mb-6 text-base-content">
              HLR: Heart-Lung Recovery Index
            </h2>
            <p className="mb-4">
              HLR captures how your body is responding to training stress at a
              physiological level. It focuses on cardiorespiratory recovery and
              autonomic balance by integrating three core signals:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
              <li>Resting heart rate (RHR)</li>
              <li>Heart rate variability (HRV)</li>
              <li>Respiratory rate (RR)</li>
            </ul>
            <p className="mb-4">
              These are established recovery markers, but they become far more
              useful when interpreted correctly. Single day readings are noisy.
              They fluctuate with sleep quality, hydration, travel, stress,
              temperature, and even meal timing.
            </p>
            <p className="mb-4">
              That is why PEAK^ does not calculate HLR from a single day.
            </p>
            <p className="mb-4">
              Instead, HLR compares your most recent 7 day average to your
              longer term baseline, drawn from up to 56 days of data (or 28 days
              when that is all that is available). This trend based approach is
              intentional.
            </p>
            <p className="mb-4">
              It allows PEAK^ to detect whether your physiology is behaving as
              expected, drifting toward strain, or showing signs of reduced
              recovery capacity, without reacting to normal day to day noise.
            </p>
            <p className="mb-4">
              HLR adds an objective layer to readiness. It does not replace how
              you feel, and it does not replace what you have been training. It
              strengthens decision making by showing whether the body appears to
              be keeping up with the demands being placed on it.
            </p>
            <p className="mb-4">
              Wearable data improves the depth of this signal, but it is not
              mandatory. Athletes without a wearable can still contribute
              meaningfully by recording resting heart rate each morning on
              waking.
            </p>
            <p className="mb-4">
              It is also important to understand that HLR is not expected to be
              static.
            </p>
            <p>
              In female athletes, autonomic and cardiorespiratory markers can
              fluctuate naturally across the menstrual cycle. Patterns such as
              higher values around ovulation and lower values approaching
              menstruation can be normal physiological responses. These changes
              are not inherently good or bad, they are context that needs to be
              interpreted alongside training phase and symptoms. This is one
              reason PEAK^ focuses on individual trends rather than population
              norms.
            </p>
          </section>

          {/* Contextual Modifiers Section */}
          <section>
            <h2 className="text-4xl font-bold font-heading mb-6 text-base-content">
              Contextual modifiers that shape readiness
            </h2>
            <p className="mb-4">
              The Peak score also accounts for factors that meaningfully change
              readiness but are often overlooked by traditional models. These
              modifiers keep the score grounded in biological reality,
              especially when training load alone does not tell the full story.
            </p>

            <h3 className="text-2xl font-bold font-heading mb-3 mt-8 text-primary">
              Illness
            </h3>
            <p className="mb-4">
              When an athlete is sick, the Peak score is reduced to reflect the
              added physiological stress placed on the immune and autonomic
              systems. Just as importantly, the score does not instantly rebound
              when symptoms resolve. The longer the illness period, the more
              gradual the return, helping guide athletes back toward full health
              before pushing intensity too early.
            </p>

            <h3 className="text-2xl font-bold font-heading mb-3 mt-8 text-primary">
              Injury
            </h3>
            <p className="mb-4">
              If an athlete records being injured for the first time, a
              reduction is applied to the Peak score based on the injury status
              selected. 'Injured can train' applies a smaller reduction to
              reflect modified capacity, while 'injured can't train' applies a
              larger reduction to reflect that normal training tolerance and
              output are not currently available. The first day of injury is
              weighted more heavily to emphasise the importance of rest and
              recovery early in the process.
            </p>
            <p className="mb-4">
              Injuries change how you move, how confident you feel, and what
              forces you can safely produce in training. PEAK^ helps the athlete
              recognise and respect that signal, so recovery is prioritised and
              a return to full training happens sooner.
            </p>

            <h3 className="text-2xl font-bold font-heading mb-3 mt-8 text-primary">
              Soreness
            </h3>
            <p className="mb-4">
              Mild soreness is normal and has only a small influence. Higher
              soreness ratings progressively reduce the score, reflecting
              reduced movement confidence, altered mechanics, and a lower
              capacity to express force, speed, and efficiency. This ensures
              readiness reflects not just tolerance for work, but quality of
              movement and performance potential.
            </p>

            <h3 className="text-2xl font-bold font-heading mb-3 mt-8 text-primary">
              Training density
            </h3>
            <p className="mb-4">
              The same weekly load performed across fewer days places more
              strain on the body due to larger spikes in demand. In these
              situations, the score is protectively lowered to flag the
              increased stress of compressed loading by the density modifier.
            </p>
            <p className="mb-4">
              The density modifier also suppresses a false signal for athletes
              that train only two or three times per week; preventing an
              artificial readiness boost that looks like a taper when no taper
              was intended. Logging light movement sessions on non-training
              days, such as walking, mobility, or recovery work, helps the data
              maintain a realistic context.
            </p>
            <p>
              Together, these modifiers make the Peak score more sensitive, more
              protective, and more aligned with how the body actually responds
              to training, health, and life stress.
            </p>
          </section>

          {/* Why it's built this way */}
          <section>
            <h2 className="text-4xl font-bold font-heading mb-6 text-base-content">
              Why the Peak score is built this way
            </h2>
            <p className="mb-4">
              When these elements are combined, the Peak score becomes more than
              a metric. It becomes a live representation of how training load,
              recovery, physiology, and context are interacting for that
              specific athlete at that specific point in time.
            </p>
            <p className="mb-4">
              As more data is collected, individual baselines strengthen.
              Patterns become clearer. Short term noise becomes easier to
              separate from meaningful change. This is why the Peak score
              improves with consistent logging.
            </p>
            <p className="mb-4">
              The Peak score is not designed to replace coaching judgement or
              athlete self awareness. It is designed to support them.
            </p>
            <p className="mb-4">
              It formalises what high quality practitioners already do
              intuitively, but with a major advantage.
            </p>
            <p className="italic text-base-content/80 border-l-4 border-primary pl-4 py-2 bg-base-200 mb-4">
              It is built on your trends over time, not assumptions, averages,
              or isolated single day readings.
            </p>
            <p>
              When readiness is viewed through this lens, load management
              becomes adaptive rather than rigid. Recovery becomes personalised
              rather than generic. Risk is identified earlier, before
              performance drops or injury occurs.
            </p>
          </section>

          {/* Final Perspective */}
          <section className="bg-base-200 p-8 md:p-12 rounded-2xl">
            <h2 className="text-4xl font-bold font-heading mb-6 text-base-content">
              Final perspective
            </h2>
            <p className="mb-4">
              The Peak score exists to make individual evidence visible,
              interpretable, and actionable. It is not trying to control
              athletes or replace coaching judgement. It is designed to improve
              decision making by showing what is happening inside the full
              system, not just one metric at a time.
            </p>
            <p className="mb-4">
              When you combine wellness, load balance, physiology, and real
              world modifiers like illness, injury, soreness, and training
              density, you get a readiness signal that actually matches how
              athletes function day to day. Over time, your baseline becomes
              clearer, your patterns become obvious, and your decisions get
              sharper.
            </p>
            <p className="mb-4">
              The goal of PEAK^ is not "perfect prediction".
            </p>
            <p className="font-semibold text-lg">
              The goal is to enable better decisions for a real human being,
              using their own data, interpreted in context.
            </p>
          </section>

          {/* References */}
          <section className="border-t border-base-300 pt-8 mt-12">
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              References
            </h2>
            <div className="space-y-3 text-sm text-base-content/80">
              <p>
                Bannister, E. W. (1991). Modeling elite athletic performance.{" "}
                <em>Physiological Testing of the High-Performance Athlete</em>,
                403-424.
              </p>
              <p>
                Hooper, S. L., & Mackinnon, L. T. (1995). Monitoring
                overtraining in athletes. <em>Sports Medicine</em>, 20(5),
                321-327.
              </p>
              <p>
                Saw, A. E., Main, L. C., & Gastin, P. B. (2016). Monitoring the
                athlete training response: Subjective self-reported measures
                trump commonly used objective measures.{" "}
                <em>British Journal of Sports Medicine</em>, 50(5), 281-291.
              </p>
              <p>
                Plews, D. J., Laursen, P. B., Stanley, J., Kilding, A. E., &
                Buchheit, M. (2013). Training adaptation and heart rate
                variability in elite endurance athletes.{" "}
                <em>European Journal of Applied Physiology</em>, 113(7),
                1631-1640.
              </p>
              <p>
                Gabbett, T. J. (2016). The training-injury prevention paradox.{" "}
                <em>British Journal of Sports Medicine</em>, 50(5), 273-280.
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-neutral text-neutral-content py-12 px-4 md:px-8 mt-24">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-groteskLogo mb-2">
                PEAK<span className="font-light text-base-content/50">^</span>
              </h3>
              <p className="text-base-content/50 text-sm font-grotesk">
                Train smarter. Perform better.
              </p>
            </div>
            <div className="text-center md:text-right">
              <p className="text-base-content/50 text-sm font-grotesk">
                © 2026 PEAK^ by Equil Labs Pty Ltd.
              </p>
              <p className="text-base-content/50 text-sm font-grotesk">
                All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PeakScorePage;
